import CloseIcon from "@mui/icons-material/Close";
import { Container, IconButton } from "@mui/material";
import React from "react";
import { FullScreenLayoutWrapper } from "../../layout/FullScreenLayout.style";
import {
  ContentComponent,
  DialogActionsStyle,
  FullScreenDialogTitleText,
  FullScreenDialogTitleWrapper,
} from "./CommonDialog.style";
import CommonDialogTitle from "./CommonDialogTitle";
import { Dialog } from "../modal/Dialog";

interface CommonDialogProps {
  isDialogOpen: boolean;
  onDialogClose: () => void;
  title: string;
  titleAlignment?: "center" | "left" | "right" | string;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  children: React.ReactNode;
  actionContent?: React.ReactNode;
  isFullScreen?: boolean;
  // Makes the content area a flex column filling Paper's actual height
  // instead of auto-sizing to content. Off by default for other callers.
  fillContentHeight?: boolean;
}

const CommonDialog = ({
  isDialogOpen = false,
  onDialogClose,
  title,
  titleAlignment = "left",
  maxWidth = "sm",
  children,
  actionContent,
  isFullScreen = false,
  fillContentHeight = false,
}: CommonDialogProps) => {
  const DialogComponent = isFullScreen ? FullScreenLayoutWrapper : Dialog;
  return (
    <DialogComponent
      open={isDialogOpen}
      onClose={onDialogClose}
      maxWidth={maxWidth}
      data-testid="common-dialog"
      fullWidth
      fullScreen={isFullScreen}
      hideBackdrop={isFullScreen}
      slotProps={
        fillContentHeight
          ? {
              // Matches MUI's own maxHeight formula, so flex:1 below has an
              // actual height to fill instead of just a cap on auto-sizing.
              paper: { sx: { height: "calc(100% - 64px)" } },
            }
          : undefined
      }
    >
      {!isFullScreen && (
        <CommonDialogTitle
          onClose={onDialogClose}
          titleAlignment={titleAlignment}
          data-testid="confirmation-dialog-title"
        >
          {title}
        </CommonDialogTitle>
      )}
      <Container
        sx={
          fillContentHeight
            ? {
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }
            : undefined
        }
      >
        {isFullScreen && (
          <FullScreenDialogTitleWrapper>
            <FullScreenDialogTitleText variant="h6">
              {title}
            </FullScreenDialogTitleText>
            <IconButton
              size="large"
              color="inherit"
              data-testid="close-export-chat"
              onClick={onDialogClose}
            >
              <CloseIcon />
            </IconButton>
          </FullScreenDialogTitleWrapper>
        )}
        <ContentComponent
          sx={
            fillContentHeight
              ? {
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }
              : { padding: 0, margin: 0 }
          }
        >
          {children}
        </ContentComponent>
        {actionContent && (
          <DialogActionsStyle>{actionContent}</DialogActionsStyle>
        )}
      </Container>
    </DialogComponent>
  );
};

export default CommonDialog;
